import {memo, useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {FilterBasicLegacyMultiSelection} from '@adv-mt/mt-filter-basic-legacy'
import FilterWrapperBasic, {filterWrapperBasicAllSelectionsId as allSelectionsId} from '@adv-mt/mt-filter-wrapper-basic'
import MultiSelect from '@adv-mt/mt-form-filter-multi-select'
import Select from '@adv-mt/mt-form-select'

import {ICONS_BY_FUEL_TYPE} from './config.js'
import {getFuelTypeLabelSelected} from './utils.js'

const DEFAULT_EMPTY_TOTAL_RESULTS = 0
const LEGACY_WRAPPER = 'legacy'

const FormFuelType = ({
  aggregations,
  initialSearch = {},
  isBasicContent = false,
  isMultiSelect = false,
  label,
  onChange,
  overlayContentRef,
  type,
  value = [0],
  withLabel,
  wrapper = LEGACY_WRAPPER
}) => {
  const {domain, i18n} = useContext(Context)

  const config = domain.get('config')

  const {specificFueltypes, fuelTypes, FILTER_NAMES, DEFAULT_FUEL_TYPE} = config.get('search')

  const TRACKING_FILTER_NAMES = config.get('TRACKING_FILTER_NAMES')
  const placeholder = i18n.t('COMMON_ALL_FUEL_TYPES')

  const hasLabel = Boolean(label)
  const defaultLabel = i18n.t('COMMON.FUEL')
  const filterLabel = hasLabel ? label : defaultLabel

  const options = specificFueltypes || fuelTypes

  const currentValue = value.length ? value : [DEFAULT_FUEL_TYPE]
  const [singleCurrentValue] = currentValue

  const hasAggregations = Boolean(aggregations)

  const getItemTotalResults = ({itemId}) =>
    aggregations.find(({id}) => id === itemId)?.totalResults || DEFAULT_EMPTY_TOTAL_RESULTS

  const localizeOptionsLabel = option => ({
    ...option,
    icon: ICONS_BY_FUEL_TYPE[option.id],
    label: i18n.t(option.label),
    ...(hasAggregations && {
      extendedInfo: i18n.n(Number(getItemTotalResults({itemId: String(option.id)})))
    })
  })

  const localizedFueltypes = options.map(localizeOptionsLabel)

  const FIRST_OPTION = {id: DEFAULT_FUEL_TYPE, label: placeholder}
  const optionsWithReset = [FIRST_OPTION, ...localizedFueltypes]

  const _handleOnChange = (e, {value}) => {
    const fuelTypeIds = Array.isArray(value) ? value : [value]

    const fuelTypeInitialSearch = initialSearch[FILTER_NAMES.FUEL_TYPE]

    const hasLastFilter = fuelTypeInitialSearch?.every(fuelTypeId => fuelTypeIds.includes(fuelTypeId))

    onChange(e, {
      field: FILTER_NAMES.FUEL_TYPE,
      value: fuelTypeIds,
      lastFilter: hasLastFilter && TRACKING_FILTER_NAMES[FILTER_NAMES.FUEL_TYPE]
    })
  }

  const handleChangeFuelType = ids => _handleOnChange(null, {value: ids})

  if (wrapper === LEGACY_WRAPPER) {
    if (isBasicContent && isMultiSelect) {
      return (
        <div className="mt-FormFuelTypeBasicContent">
          <FilterBasicLegacyMultiSelection
            onChange={handleChangeFuelType}
            options={localizedFueltypes}
            values={value}
          />
        </div>
      )
    }

    return isMultiSelect ? (
      <MultiSelect
        field={FILTER_NAMES.FUEL_TYPE}
        initialSearch={initialSearch}
        label={filterLabel}
        onChange={_handleOnChange}
        options={localizedFueltypes}
        placeholder={placeholder}
        value={value}
      />
    ) : (
      <Select
        field={FILTER_NAMES.FUEL_TYPE}
        initialSearch={initialSearch}
        label={filterLabel}
        onChange={_handleOnChange}
        options={optionsWithReset}
        value={singleCurrentValue}
      />
    )
  }

  return (
    <FilterWrapperBasic
      filters={[
        {
          allSelectionsLabel: i18n.t('COMMON_ALL_FUEL_TYPES'),
          label: i18n.t('COMMON.FUEL'),
          name: FILTER_NAMES.FUEL_TYPE,
          options: localizedFueltypes,
          showLabel: true,
          type
        }
      ]}
      group={`${FILTER_NAMES.FUEL_TYPE}Group`}
      legend={i18n.t('COMMON.FUEL')}
      onChange={({value}) => {
        _handleOnChange(null, {value: value.filter(id => id !== allSelectionsId)})
      }}
      overlayContentRef={overlayContentRef}
      placeholder={
        value.length
          ? getFuelTypeLabelSelected({fuelOptions: localizedFueltypes, value})
          : i18n.t('COMMON_ALL_FUEL_TYPES')
      }
      showLegend={withLabel}
      value={{[FILTER_NAMES.FUEL_TYPE]: value.length ? value : [allSelectionsId]}}
      wrapper={wrapper}
    />
  )
}

FormFuelType.displayName = 'FormFuelType'
FormFuelType.propTypes = {
  aggregations: PropTypes.array,
  initialSearch: PropTypes.object,
  isBasicContent: PropTypes.bool,
  isMultiSelect: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  overlayContentRef: PropTypes.node,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
  withLabel: PropTypes.bool,
  wrapper: PropTypes.string
}

const FormMultiFuelType = props => {
  return <FormFuelType {...props} isMultiSelect />
}
FormMultiFuelType.displayName = 'FormMultiFuelType'

const FormMultiFuelTypeBasicContent = props => <FormFuelType {...props} isBasicContent isMultiSelect />
FormMultiFuelTypeBasicContent.displayName = 'FormMultiFuelTypeBasicContent'

const MemoFormFuelType = memo(FormFuelType)
const MemoFormMultiFuelType = memo(FormMultiFuelType)
const MemoFormMultiFuelTypeBasicContent = memo(FormMultiFuelTypeBasicContent)

export {MemoFormMultiFuelType as FormMultiFuelType, MemoFormMultiFuelTypeBasicContent as FormMultiFuelTypeBasicContent}

export default MemoFormFuelType
